<template>
  <div class="Evaluation-modal-overlay">
    <transition name="modal-expand">
      <div v-if="isVisible" class="Evaluation-modal">
        <font-awesome-icon class="window-close-icon" :icon="['fas', 'window-close']" @click="close" />
        <h3 class="Evaluation-modal-title">{{ modalTitle }}</h3>
        <div v-if="isDisabled && isAssessor" class="flex-grow-1 d-flex justify-content-center" style="margin-bottom: 20px;">
          <div class="expired-box">
            This logbook is locked. The evaluation can no longer be completed.
          </div>
        </div>
        <div v-if="isAssessor && !hasMetTaskRequirement" class="flex-grow-1 d-flex justify-content-center" style="margin-bottom: 20px;">
          <div class="expired-box" style="margin-top:10px;">
            Minimum {{ minTasksRequired }} main tasks are required to submit this evaluation. The trainee has completed {{ completedTasks }} main tasks.
          </div>
        </div>
        <table class="assessment-table">
          <thead>
            <tr>
              <th rowspan="2">Competence Criteria</th>
              <th v-if="isAssessor" colspan="2">Performance result</th>
              <th v-else colspan="1" style="text-align:center;">Performance result</th>
            </tr>
            <tr v-if="isAssessor">
              <th>
                Satisfactory
                <div style="border:1px solid grey; font-weight:100;border-radius: 5px;">
                  <input type="checkbox" id="selectAllSatisfactory" @change="toggleAllSatisfactory"
                    :disabled="isCheckboxDisabled || isDisabled">Select All
                </div>
              </th>
              <th>Unsatisfactory</th>
            </tr>
          </thead>
          <tbody>
            <!-- Inside the <tbody> tag -->
            <tr v-for="(item) in criteriaResults" :key="item.criterionId">
              <td>{{ criteria[item.criterionId - 1] }}</td>
              <template v-if="isAssessor">
              <td>
                <input type="radio" :id="`satisfactory${item.criterionId}`" :name="`result${item.criterionId}`"
                  value="satisfactory" v-model="item.result" :disabled="isSubmitted > 0 || isDisabled || isLockedParentSatisfactory(item.criterionId)">
                <label :for="`satisfactory${item.criterionId}`"></label>
              </td>
              <td>
                <input type="radio" :id="`unsatisfactory${item.criterionId}`" :name="`result${item.criterionId}`"
                  value="unsatisfactory" v-model="item.result" :disabled="isSubmitted > 0 || isDisabled || isLockedParentSatisfactory(item.criterionId)">
                <label :for="`unsatisfactory${item.criterionId}`"></label>
              </td>
              </template>
              <template v-else>
                <td style="text-align: center;" :class="{
                  satisfactory: item.result === 'satisfactory',
                  unsatisfactory: item.result === 'unsatisfactory'
                }">
                  {{ item.result ? item.result.charAt(0).toUpperCase() + item.result.slice(1) : '-' }}
                </td>
              </template>
            </tr>
          </tbody>
        </table>
        <div v-if="isRemedialOutcome" class="remedial-comment-section" style="margin: 15px 0;">          <label for="remedialComment" style="font-weight:bold; display:block; margin-bottom:5px;">
            Reason for Remedial Result:
          </label>
          <textarea
            v-if="isAssessor"
            id="remedialComment"
            v-model="comment"
            rows="3"
            style="width:100%; resize:vertical; box-sizing:border-box; padding:6px;"
            placeholder="Please provide a reason for the remedial result..."
            :disabled="isSubmitted > 0 || isDisabled"
          ></textarea>
          <div v-else style="white-space:pre-wrap; border:1px solid #ddd; padding:8px; border-radius:4px; background:#fff;">
            {{ comment || '-' }}
          </div>
        </div>
        <div style="background-color:#f2f2f2; padding-top: 10px; border: 1px solid #ddd;">
          <div class="assessment-result">
            <table style="width: 100%;">
              <tbody>
                <tr>
                  <td style="width: 50%; font-weight: bold; text-align: right; padding-right: 15px;">
                    RESULT OF THE ASSESSMENT
                  </td>
                  <td style="width: 50%; border:none; text-align: left; padding-left: 15px;"
                    :style="assessmentResultStyle">
                    <span v-if="assessmentResult" class="assessment-result-text">{{ assessmentResult.toUpperCase() }}</span>
                    <span v-else style="border:none">-</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="assessment-container">
            <div class="table-summary">
              <table class="task-performance">
                <tbody>
                  <tr>
                    <th style="font-size:14px; padding-left: 4px;">Number of Main Tasks in this practical training element</th>
                    <td style="font-weight: bold; text-align: center; width:10%;">{{ tasksCount }}</td>
                  </tr>
                  <tr>
                    <td style="width:40%; font-size:14px; padding-left: 4px;">Number of Main Tasks performed by the Trainee (mini {{ minTasksRequired }} to pass)</td>
                    <td style="text-align: center;">{{ completedTasks }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="signature-block">
              <table class="signature-table">
                <tbody>
                  <tr v-if="isTrainee" class="tr-signature">
                    <td style="width:250px; font-weight:bold">TRAINEE SIGNATURE:</td>
                    <td style="width:250px;">
                      <canvas v-if="!usingCurrentSignature" ref="traineeSignaturePadCanvas" id="trainee-signature-pad"
                        class="signature-pad" width="280" height="80"></canvas>
                      <div v-if="signature && usingCurrentSignature" class="signature-options-message mb-2"
                        style="font-weight: bold;">
                        <img :src="'data:image/png;base64,' + signature.user_image_base64" alt="User Signature"
                          class="selected-image" />
                      </div>
                      <div v-if="signature && !usingCurrentSignature && isSubmitted < 2" style="margin-top: 5px;">
                        <a href="#" @click.prevent="useCurrentSignature"
                          style="color: blue; text-decoration: underline;">
                          Use Current Signature
                        </a>
                      </div>
                      <div v-if="signature && usingCurrentSignature" style="margin-top: 5px;">
                        <a href="#" @click.prevent="removeCurrentSignature"
                          style="color: blue; text-decoration: underline;">
                          Remove Current Signature
                        </a>
                      </div>
                    </td>
                    <td v-if="traineeName" style="padding-left: 50px;"><b>Trainee Name:</b> {{ traineeName }}</td>
                    <td v-else></td>
                  </tr>
                  <tr v-if="isAssessor" class="trainee-signature">
                    <td style="width:250px; font-weight:bold;">TRAINEE SIGNATURE:</td>
                    <td v-if="traineeSignature" style="width: 300px;">
                      <div class="signature-image-wrapper" :class="{ 'disabled': isSubmitted > 0 }">
                        <img v-if="traineeSignature" :src="traineeSignature" alt="Trainee Signature"
                          class="signature-image" width="280" height="80" />
                        <!-- Transparent div to overlay on top of the image -->
                        <div class="signature-overlay" v-if="isSubmitted > 0"></div>
                      </div>
                    </td>
                    <td v-else style="cursor: not-allowed; width: 300px;">
                      <canvas ref="traineeSignaturePadCanvas" id="trainee-signature-pad" class="signature-pad"
                        width="280" height="80"></canvas>
                    </td>
                    <td v-if="traineeName" style="padding-left: 50px;"><b>Trainee Name:</b> {{ traineeName }}</td>
                    <td v-else></td>
                  </tr>
                  <tr v-if="isTrainee" class="assessor-signature">
                    <td style="font-weight:bold;">ASSESSOR SIGNATURE:</td>
                    <td style="width: 300px;">
                      <div v-if="assessorSignature" class="signature-image-wrapper">
                        <img
                          :src="assessorSignature"
                          alt="Assessor Signature"
                          class="signature-image"
                          width="280"
                          height="80"
                        />
                      </div>
                      <div v-else class="signature-pad-placeholder">
                        -
                      </div>
                    </td>
                    <td v-if="assessorName" style="padding-left: 50px;">
                      <b>Assessor Name:</b> {{ assessorName }}
                    </td>
                    <td v-else></td>
                  </tr>
                  <tr v-if="isAssessor" class="signature">
                    <td style="font-weight:bold">ASSESSOR SIGNATURE:</td>
                    <td v-if="hasMetTaskRequirement">
                      <canvas v-if="!usingCurrentSignature" ref="assessorSignaturePadCanvas" id="signature-pad"
                        class="signature-pad" width="280" height="80"></canvas>
                      <div v-if="signature && usingCurrentSignature" class="signature-options-message mb-2"
                        style="font-weight: bold;">
                        <img :src="'data:image/png;base64,' + signature.user_image_base64" alt="User Signature"
                          class="selected-image" />
                      </div>
                      <div v-if="signature && !usingCurrentSignature && !isSubmitted && !isDisabled"
                        style="margin-top: 5px;">
                        <a href="#" @click.prevent="useCurrentSignature"
                          style="color: blue; text-decoration: underline;">
                          Use Current Signature
                        </a>
                      </div>
                      <div v-if="signature && usingCurrentSignature" style="margin-top: 5px;">
                        <a href="#" @click.prevent="removeCurrentSignature"
                          style="color: blue; text-decoration: underline;">
                          Remove Current Signature
                        </a>
                      </div>
                    </td>
                    <td v-else colspan="2">
                      <p style="color: red; margin-top: 15px;">Trainee must complete minimum {{ passRate }}% of Main Tasks before an Assessor can sign the Evaluation form</p>
                    </td>
                    <td v-if="assessorName" style="padding-left: 50px;"><b>Assessor Name:</b> {{ assessorName }}</td>
                    <td v-else></td>
                  </tr>
                  <tr class="signature">
                    <td style="font-weight:bold;">DATE:</td>
                    <td><input type="date" id="date" name="date" v-model="date" :max="today" style="width: 280px;"
                        :disabled="isSubmitted > 0 || isDisabled || isTrainee" /></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div class="button-container">
          <button class="ins-close-button" @click="close">Close</button>
          <div v-if="isAssessor" class="right">
            <button class="ins-clear-button" @click="clearSignature" :disabled="isSubmitted > 0 || isDisabled">Clear Signature</button>
            <!-- <button class="ins-save-button" @click="handleSave" :disabled="isSubmitted > 0 || isDisabled">Save</button> -->
            <button class="ins-submit-button" @click="submitEvaluation"
              :disabled="isSubmitted > 0 || isDisabled">Submit</button>
          </div>
          <div v-if="isTrainee" class="right">
            <button class="ins-clear-button" @click="clearSignature" :disabled="isSubmitted > 1">Clear Signature</button>
            <button class="ins-submit-button" @click="submitTraineeSignature"
              :disabled="isSubmitted > 1">Submit</button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from 'axios';
import SignaturePad from 'signature_pad';
import { ENFORCE_EVALUATION_MIN_TASKS } from '@/config/featureFlags';

export default {
  props: {
    mode: {
      type: String,
      required: true, // 'assessor' or 'trainee'
      validator: v => ['assessor', 'trainee'].includes(v)
    },
    tasksCount: { type: Number, required: true },
    completedTasks: Number,
    fetchedData: Object,
    evaluationId: Number,
    logbookId: Number,
    locked: Boolean,
    propTasks: {
      type: Array,
      default: () => []
    },
    evaluationType: {
      type: String,
      default: 'continuous'
    },
    parentEvaluationId: {
      type: Number,
      default: null
    },
    parentEvaluationData: {
      type: Object,
      default: null
    },
    trainee: {
      type: Object,
      required: true
    },
    passRate: {
      type: Number,
      default: 50
    }
  },
  data() {
    return {
      isVisible: false,
      isSubmitted: 0,
      date: '',
      today: new Date().toISOString().split('T')[0],
      criteria: [
        'Consults the correct documentation',
        'Scans the environment before starting and during the task to ensure safety',
        'Reads/interprets the safety warning correctly',
        'Informs/communicates with appropriate people correctly',
        'Interprets the reports, indications and documentation correctly',
        'Follows the procedure steps',
        'Reacts properly to changes during task performance to ensure safety',
        'Takes into account interactions between systems',
        'Restores the aircraft back to initial condition (or appropriate condition depending on the circumstances)',
      ],
      criteriaResults: new Array(9).fill(null).map((_, i) => ({
        criterionId: i + 1,
        result: null
      })),
      traineeName: '',
      assessorName: '',
      traineeSignature: '',
      assessorSignature: '',
      traineeSignaturePad: null,
      assessorSignaturePad: null,
      signature: null,
      books: [],
      usingCurrentSignature: false,
      mergedTasks: [],
      comment: '',
    }
  },
  computed: {
    isAssessor() {
      return this.mode === 'assessor';
    },
    isTrainee() {
      return this.mode === 'trainee';
    },
    currentUserId() {
      return localStorage.getItem('user_id');
    },
    currentRole() {
      return this.isAssessor ? 'instructor' : 'trainee';
    },
    resolvedEvaluationType() {
      return this.fetchedData?.evaluation_type || this.evaluationType || 'continuous';
    },
    resolvedParentEvaluationId() {
      return this.fetchedData?.parent_evaluation_id || this.parentEvaluationId || null;
    },
    resolvedTraineeId() {
      return this.trainee?.user_id || this.fetchedData?.trainee_id || null;
    },
    modalTitle() {
      return this.resolvedEvaluationType === 'remedial'
        ? 'REMEDIAL EVALUATION SHEET'
        : 'CONTINUOUS EVALUATION SHEET';
    },
    assessmentResult() {
      const allSatisfactory = this.criteriaResults.every(item => item.result === 'satisfactory');
      const anyUnsatisfactory = this.criteriaResults.some(item => item.result === 'unsatisfactory');

      if (allSatisfactory) {
        return 'successful';
      }

      if (anyUnsatisfactory) {
        return this.resolvedEvaluationType === 'remedial' ? 'unsuccessful' : 'remedial';
      }

      return null;
    },
    assessmentResultStyle() {
      switch (this.assessmentResult) {
        case 'successful':
          return { color: 'green', fontWeight: 'bold', border: 'none' };
        case 'remedial':
        case 'unsuccessful':
          return { color: 'red', fontWeight: 'bold', border: 'none' };
        default:
          return {};
      }
    },
    isCheckboxDisabled() {
      return this.isSubmitted > 0;
    },
    isDisabled() {
      return this.locked === 1;
    },
    unsignedCompletedTasks() {
      // Filter tasks that are marked as completed but have no trainee signature
      return this.mergedTasks.filter(
        task => task.completed && !task.t_signature_id
      );
    },
    unsignedCompletedCount() {
      return this.unsignedCompletedTasks.length;
    },
    unsignedInstTasks() {
      return this.mergedTasks.filter(
        task => task.ins_so && !task.ins_signature_id
      );
    },
    unsignedInstCount() {
      return this.unsignedInstTasks.length;
    },
    minTasksRequired() {
      return Math.ceil(this.tasksCount * (this.passRate / 100));
    },
    hasMetTaskRequirement() {
      if (!ENFORCE_EVALUATION_MIN_TASKS) return true;
      return this.completedTasks === null || this.completedTasks >= this.minTasksRequired;
    },
    isRemedialOutcome() {
      return this.assessmentResult === 'remedial' || this.assessmentResult === 'unsuccessful';
    },
  },
  watch: {
    fetchedData(newVal) {
      console.log('Received fetched data:', newVal);
      this.populateEvaluationData(newVal);
    },
    isSubmitted(newValue, oldValue) {
      const canvas = this.$refs.assessorSignaturePadCanvas;

      if (newValue > 0) {
        if (this.assessorSignaturePad) {
          this.assessorSignaturePad.off();
        }
        if (canvas) {
          canvas.style.opacity = '0.5';
        }
      } else if (oldValue > 0 && newValue === 0) {
        this.$nextTick(() => {
          this.initializeSignaturePads();
        });
      }
    },
    date(value) {
      if (value > this.today) {
        this.date = this.today;
      }
    },
  },
  methods: {
    close(locked = false) {
      this.$emit('close', locked);
    },
    isLockedParentSatisfactory(criterionId) {
      return (
        this.resolvedEvaluationType === 'remedial' &&
        this.parentEvaluationData?.criteria?.[criterionId] === 'satisfactory'
      );
    },
    toggleAllSatisfactory(event) {
      const checked = event.target.checked;
      this.criteriaResults.forEach(c => {
        c.result = checked ? 'satisfactory' : null
      });
    },
    useCurrentSignature() {
      this.usingCurrentSignature = true;
    },
    removeCurrentSignature() {
      this.usingCurrentSignature = false;
      this.$nextTick(() => this.initializeSignaturePads());
    },
    clearSignature() {
      if (this.isAssessor && this.isSubmitted === 0) {
        if (this.assessorSignaturePad) {
          this.assessorSignaturePad.clear();
        }
        this.assessorName = null;
        this.assessorSignature = null;
        this.usingCurrentSignature = false;
      }

      if (this.isTrainee && this.traineeSignaturePad && this.isSubmitted === 1) {
        this.traineeSignaturePad.clear();
      }
    },
    setupPad(canvasRefName, existingSignature, padKey, disabled) {
      const canvas = this.$refs[canvasRefName];
      if (!canvas) return;

      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      canvas.width = canvas.offsetWidth * ratio;
      canvas.height = canvas.offsetHeight * ratio;
      const ctx = canvas.getContext('2d');
      ctx.scale(ratio, ratio);

      const pad = new SignaturePad(canvas, {
        backgroundColor: 'rgb(255, 249, 245)',
        minWidth: 0.5,
        maxWidth: 1.5
      })

      if (existingSignature) {
        const img = new Image()
        img.onload = () => {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0, canvas.offsetWidth, canvas.offsetHeight);
        }
        img.src = existingSignature;
      }

      if ((disabled && this.isAssessor) || this.isSubmitted === 2) {
        pad.off();
        canvas.style.pointerEvents = 'none';
        canvas.style.opacity = '0.5';
      } else {
        canvas.style.opacity = '1';
      }

      this[padKey] = pad;
    },
    initializeSignaturePads() {
      if (this.isAssessor && !this.usingCurrentSignature) {
        this.setupPad(
          'assessorSignaturePadCanvas',
          this.assessorSignature,
          'assessorSignaturePad',
          this.isSubmitted > 0 || this.isDisabled
        );
      }
      if (this.isTrainee && !this.usingCurrentSignature) {
        this.setupPad(
          'traineeSignaturePadCanvas',
          this.traineeSignature,
          'traineeSignaturePad',
          this.isSubmitted === 2 || this.isDisabled
        );
      }
    },
    destroySignaturePads() {
      if (this.assessorSignaturePad) {
        this.assessorSignaturePad.off();
        this.assessorSignaturePad = null;
      }
      if (this.traineeSignaturePad) {
        this.traineeSignaturePad.off();
        this.traineeSignaturePad = null;
      }
    },
    populateEvaluationData(evaluationData) {
      const isRemedial =
        (evaluationData?.evaluation_type || this.evaluationType) === 'remedial';

      const parentCriteria = this.parentEvaluationData?.criteria || {};
      const currentCriteria = evaluationData?.criteria || {};

      if (isRemedial && parentCriteria && typeof parentCriteria === 'object') {
        this.criteriaResults = this.criteriaResults.map(item => {
          const parentResult = parentCriteria[item.criterionId];
          const currentResult = currentCriteria[item.criterionId];

          return {
            criterionId: item.criterionId,
            result: parentResult === 'satisfactory'
              ? 'satisfactory'
              : currentResult || null
          };
        });
      } else if (currentCriteria && typeof currentCriteria === 'object') {
        this.criteriaResults = Object.entries(currentCriteria).map(([criterionId, result]) => ({
          criterionId: parseInt(criterionId),
          result
        }));
      }

      this.traineeSignature = evaluationData.trainee_signature || '';
      this.assessorSignature = evaluationData.assessor_signature || '';
      this.date = evaluationData.date || '';
      this.isSubmitted = evaluationData.is_submitted ?? 0;
      this.comment = evaluationData.comment || '';

      this.traineeName = evaluationData.trainee
        ? `${evaluationData.trainee.first_name || ''} ${evaluationData.trainee.last_name || ''}`.trim()
        : this.trainee
          ? `${this.trainee.first_name || ''} ${this.trainee.last_name || ''}`.trim()
          : '';

      this.assessorName = evaluationData.instructor
        ? `${evaluationData.instructor.first_name || ''} ${evaluationData.instructor.last_name || ''}`.trim()
        : this.assessorName;
    },
    async getSignature() {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const userId = parseInt(this.currentUserId);
      try {
        const response = await axios.get(`/api/store_image/fetch_image/${userId}`, { headers })
        this.books = response.data;
        this.signature = this.books.find(
          item => parseInt(item.user_id) === userId && item.role === this.currentRole
        );
        console.log('signature', this.signature);
      } catch (error) {
        console.error('Error fetching signature:', error);
      }
    },
    getActiveSignatureDataUrl() {
      if (this.usingCurrentSignature && this.signature?.user_image_base64) {
        return `data:image/png;base64,${this.signature.user_image_base64}`;
      }
      if (this.isAssessor && this.assessorSignaturePad && !this.assessorSignaturePad.isEmpty()) {
        return this.assessorSignaturePad.toDataURL();
      }
      if (this.isTrainee && this.traineeSignaturePad && !this.traineeSignaturePad.isEmpty()) {
        return this.traineeSignaturePad.toDataURL();
      }
      return '';
    },
    async saveAssessorEvaluation(isSubmitting = 0) {
      const assessorSign = this.getActiveSignatureDataUrl() || this.assessorSignature || null;

      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const payload = {
        logbook_id: Number(this.logbookId),
        trainee_id: Number(this.resolvedTraineeId),
        instructor_id: assessorSign ? Number(this.currentUserId) : null,
        evaluation_type: this.resolvedEvaluationType,
        attempt_number: this.resolvedEvaluationType === 'remedial' ? 2 : 1,
        parent_evaluation_id: this.resolvedParentEvaluationId,
        criteria: Object.fromEntries(this.criteriaResults.map(r => [r.criterionId, r.result])),
        result: this.assessmentResult,
        comment: this.isRemedialOutcome ? this.comment : null,
        date: this.date,
        assessor_signature: assessorSign,
        is_submitted: Number(isSubmitting),
      }

      const req = this.evaluationId
        ? axios.put(`/api/evaluations/${this.evaluationId}`, payload, { headers })
        : axios.post('/api/evaluations', payload, { headers });

      await req;

      const shouldLock =
        (this.resolvedEvaluationType === 'continuous' && this.assessmentResult === 'successful') ||
        (this.resolvedEvaluationType === 'remedial' && payload.is_submitted === 1);

      if (shouldLock) {
        await axios.put(`/api/logbooks/${parseInt(this.logbookId)}`, { locked: 1 }, { headers });
        this.close(true);
      } else {
        this.close();
      }
    },
    async submitTraineeSignature() {
      const traineeSign = this.getActiveSignatureDataUrl();
      if (!traineeSign) {
        alert('Please provide a signature before submitting.');
        return;
      }

      const confirmed = confirm('Once submitted, the form cannot be edited. Are you sure you want to submit?')
      if (!confirmed) return;

      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      await axios.put(`/api/evaluations/${this.evaluationId}`, {
        logbook_id: Number(this.logbookId || localStorage.getItem('logbook_id')),
        trainee_id: Number(this.currentUserId),
        is_submitted: 2,
        trainee_signature: traineeSign,
      }, { headers })

      this.close();
    },
    async handleSave() {
      await this.saveAssessorEvaluation(0);
    },
    async submitEvaluation() {
      if (!this.hasMetTaskRequirement) {
        alert(`You must complete at least ${this.minTasksRequired} tasks before submitting the evaluation.`);
        return;
      }

      const isComplete = this.criteriaResults.every(c => c.result) && this.date && this.assessmentResult;
      if (!isComplete) {
        alert('Please fill all the fields before submitting.');
        return;
      }

      if (this.isRemedialOutcome && !this.comment.trim()) {
        alert('Please provide a reason for the remedial result before submitting.');
        return;
      }

      if (!this.getActiveSignatureDataUrl() && !this.assessorSignature) {
        alert('Please provide a signature before submitting.');
        return;
      }

      if (this.unsignedCompletedTasks.length > 0) {
        const proceed = confirm(
          `There are ${this.unsignedCompletedTasks.length} tasks signed off with no Trainee signature.\n\nOnce the evaluation is submitted, signatures can not be added to tasks.\nDo you wish to continue?`
        );
        if (!proceed) return;
      }

      if (this.unsignedInstTasks.length > 0) {
        const proceed = confirm(
          `There are ${this.unsignedInstTasks.length} tasks signed off with no Instructor signature.\n\nOnce the evaluation is submitted, signatures can not be added to tasks.\nDo you wish to continue?`
        )
        if (!proceed) return
      }

      const confirmed = confirm('Once submitted, the form cannot be edited and the logbook will become locked. Are you sure you want to submit?');
      if (!confirmed) return;

      this.isSubmitted = 1;
      await this.saveAssessorEvaluation(1);
    },
  },
  mounted() {
    this.isVisible = true;
    if (this.fetchedData) {
      this.populateEvaluationData(this.fetchedData);
    } else if (this.resolvedEvaluationType === 'remedial' && this.parentEvaluationData) {
      // No existing remedial record yet — pre-fill from parent criteria only
      const parentCriteria = this.parentEvaluationData?.criteria || {};
      this.criteriaResults = this.criteriaResults.map(item => ({
        criterionId: item.criterionId,
        result: parentCriteria[item.criterionId] === 'satisfactory' ? 'satisfactory' : null
      }));
    }
    if (!this.fetchedData && this.trainee) {
      this.traineeName = `${this.trainee.first_name} ${this.trainee.last_name}`;
    }
    this.getSignature();
    this.$nextTick(() => {
      setTimeout(() => {
        this.initializeSignaturePads();
      }, 2000); // 100ms usually enough for DOM paint
    });
    if (!this.date) {
      this.date = this.today;
    }
    this.mergedTasks = this.propTasks;
  },
  beforeUnmount() { // or beforeDestroy() for Vue 2
    this.destroySignaturePads();
  }
}
</script>

<style scoped>
.Evaluation-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  /* Semi-transparent background */
  display: flex;
  justify-content: center;
  z-index: 1000;
}

.Evaluation-modal {
  background-color: #ffffff;
  overflow-y: auto;
  /* Enables vertical scrolling if needed */
  margin-top: 12px;
  max-height: 97vh;
  width: min(1300px, 90vw);
  padding: 10px;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.2);
  position: fixed;
  display: flex;
  flex-direction: column;
  overflow-wrap: break-word;
  padding: 20px;
}

.modal-title {
  text-align: center;
  margin-bottom: 20px;
}

.assessment-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 20px;
  align-items: center;
}

.assessment-table th,
.assessment-table td {
  border: 1px solid #ddd;
  padding: 5px;
  text-align: left;
  padding-left: 8px;
}

.assessment-table thead th {
  background-color: #f2f2f2;
}

.signature-section {
  margin-top: 20px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}

.signature-item label {
  width: 40%;
  display: block;
  margin-bottom: 5px;
}

.signature-placeholder,
.date-placeholder {
  border: 1px solid #ddd;
  padding: 5px;
  min-height: 30px;
  /* Adjust as needed */
}

.button-container {
  display: flex;
  justify-content: space-between;
  /* Aligns items to the right */
  align-items: center;
  margin-top: 20px;
  /* Adjust as needed */
}

.ins-save-button {
  background-color: #007bff;
  border-color: #007bff;
  color: white;
  border: none;
  cursor: pointer;
  border-radius: 5px;
  padding: 5px 10px;
  margin-left: 20px;
  width: 100%;
  min-width: 90px;
  max-width: 100px;
}

.ins-submit-button {
  background-color: brown;
  color: white;
  border: none;
  cursor: pointer;
  border-radius: 5px;
  padding: 5px 10px;
  margin-left: 20px;
  min-width: 100px;
  max-width: 120px;
}

.ins-close-button {
  background-color: #6c757d;
  border: 1px solid #4d5358;
  color: white;
  padding: 5px 20px;
  border-radius: 5px;
}

.ins-close-button:hover {
  background-color: #545a5f;
}

.ins-clear-button {
  background-color: #ee9581;
  color:white;
  font-size: 16px;
  border-radius: 5px;
  margin-left: 20px;
  width: 150px;
  border: 1px solid #c7715e;
  height: 40px;
  padding: 0px;
}

.ins-clear-button:hover {
  background-color: #e47a62;
}

.ins-save-button:disabled,
.ins-submit-button:disabled,
.ins-clear-button:disabled {
  background-color: rgb(216, 216, 216);
  cursor: not-allowed;
  color: grey;
  border: none;
}

.ins-save-button:disabled:hover,
.ins-submit-button:disabled:hover,
.ins-clear-button:disabled:hover {
  background-color: rgb(216, 216, 216);
  cursor: not-allowed;
  color: grey;
  border: none;
}

.ins-submit-button:hover {
  background-color: rgb(129, 33, 33);
}

.assessment-result {
  width: 100%;
}

.assessment-result-text {
  font-size: 16px !important;
}

.table-summary {
  width: 100%;
}

.task-performance {
  width: 50%;
  border-collapse: collapse;
  text-align: left;
  background-color: #f2f2f2;
  margin-left: 20px;
}

.task-performance th,
.task-performance td {
  border: 1px solid black;
  padding-left: 8px;
}

.task-performance th {
  /* Adjust the color to match your header background */
  font-weight: bold;
}


.task-performance input[type="text"] {
  width: 100%;
  border: none;
  padding: 4px;
  text-align: center;
  background-color: #f2f2f2;
}

.assessment-container {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  margin-top: 20px;
  padding-bottom: 15px;
}


.signature-block {
  width: 100%;
  display: flex;
  flex-direction: column;
  padding-left: 30px;
}


.signature-table {
  width: 100%;
  margin-top: 20px;
}

.signature-table td {
  padding: 4px;
  vertical-align: middle;
  text-align: left;
}

.signature-table .signature {
  text-align: left;
  /* Aligns the text to the left */
}

.trainee-signature {
  text-align: left;
  width: 30%;
}

.signature-table input[type="text"],
.signature-table input[type="date"] {
  width: 100%;
  border: none;
  border-bottom: 1px solid black;
  border-radius: 0px;
  background-color: transparent;
  outline: none;
  height: 20px;
}

@media (max-width: 768px) {

  /* Adjust the max-width as needed for iPad or other tablet sizes */
  .Evaluation-modal {
    padding: 5px;
    font-size: 0.9em;
    /* Reduce font size */
  }

  /* Further adjustments for other elements */
}


#selectAllSatisfactory {
  margin-left: 5px;
  border: 1px solid rgb(206, 13, 13);
}

.signature-pad {
  border-bottom: 1px solid #000;
  border-radius: 5px;
  width: 280px;
  height: 80px;
}

.right {
  display: flex;
  justify-content: flex-end;
}

/* Add this CSS to your stylesheet */
.signature-image-wrapper {
  position: relative;
  display: inline-block;
  opacity: 0.5;
}

.signature-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: transparent;
  pointer-events: none;
  /* Prevents interaction with the overlay */
}

.disabled {
  opacity: 0.5;
  /* Optionally reduce opacity to indicate disabled state */
}

.card-body-top {
  padding: 10px;
  background-color: #bebebe;
  border: 1px solid #797979;
  border-radius: 4px;
}

.selected-image-heading {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #222;
}

.selected-image {
  width: 280px;
  max-height: 80px;
  margin-bottom: 4px;
  border: 2px solid #007bff;
  border-radius: 6px;
  background-color: #FAF9F5;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
}

a.use-signature-link {
  color: #007bff;
  cursor: pointer;
  font-size: 13px;
}

a.use-signature-link:hover {
  text-decoration: underline;
}

.unsatisfactory {
  color: red;
}
</style>
